'use client'

import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import classNames from 'classnames'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import type { Product } from '@/@types/productsType'
import { Skeleton } from '../ui'
import Link from 'next/link'
import { MdErrorOutline, MdOutlineImageNotSupported } from 'react-icons/md'
import { addCartClient, changeQtyClient } from '@/lib/actions/client'
import { useCartStore } from '@/store/useCartStore'
import { FaMinus, FaPlus } from 'react-icons/fa6'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

interface ProductCardSliderProps extends Product {
    isLoading?: boolean
}

export const ProductCardSlider = (product: ProductCardSliderProps) => {
    const touchStartX = useRef(0)
    const touchDeltaX = useRef(0)
    const {
        name,
        description,
        images,
        variants,
        isLoading,
        latin,
        local_main_image,
    } = product

    const imagesSafe = [
    ...(local_main_image ? [local_main_image] : []),
    ...(images ?? []).filter((img) => img !== local_main_image),
]

    const mainVariant = variants[0] ?? null
    const price = mainVariant?.price ?? 0
    const vendorCode = mainVariant?.vendorCode ?? ''

    const variantId = mainVariant?.id ?? null

    const cartItem = useCartStore((state) =>
        state.items?.find((i) => i.variant_id === variantId),
    )
    const quantity = cartItem?.quantity ?? 1

    const [current, setCurrent] = useState(0)
    const [imgError, setImgError] = useState<boolean[]>([])

    useEffect(() => {
    setImgError(Array(imagesSafe.length).fill(false))
}, [imagesSafe.length])

    const containerRef = useRef<HTMLDivElement | null>(null)
    const [isAdd, setIsAdd] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        // if (!images?.length || !containerRef.current) return

      
        // const zoneWidth = rect.width / images.length
        // const index = Math.floor(x / zoneWidth)
        // setCurrent(Math.min(Math.max(index, 0), images.length - 1))
        if (!imagesSafe.length || !containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const zoneWidth = rect.width / imagesSafe.length
        const x = e.clientX - rect.left
        const index = Math.floor(x / zoneWidth)

        setCurrent(Math.min(Math.max(index, 0), imagesSafe.length - 1))
    }

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX
    }

    const onTouchMove = (e: React.TouchEvent) => {
        const currentX = e.touches[0].clientX
        touchDeltaX.current = currentX - touchStartX.current
    }

    const onTouchEnd = () => {
        const threshold = 40

        if (
            touchDeltaX.current < -threshold &&
            current < imagesSafe.length - 1
        ) {
            setCurrent((p) => p + 1)
        }

        if (touchDeltaX.current > threshold && current > 0) {
            setCurrent((p) => p - 1)
        }

        touchDeltaX.current = 0
    }

    const handleImgError = (index: number) => {
        const newErrors = [...imgError]
        newErrors[index] = true
        setImgError(newErrors)
    }

    const handleAdd = async (
        variantId: string,
        qty: number,
        e?: React.MouseEvent,
    ) => {
        e?.stopPropagation()
        e?.preventDefault()
        const res = await addCartClient(variantId, qty)
        if (!res || res.error) {
            setIsError(true)
            console.log('Ошибка при добавлении товара!', res)
            return
        } else {
            setIsAdd(true)
            useCartStore.getState().loadItems()
        }

        // console.log(res, 'В КАРТОЧКЕ ТОВАРА')
    }
    const changeQty = async (itemId: string, newQty: number) => {
        if (newQty < 1) return
        const res = await changeQtyClient(itemId, newQty)

        if (res?.error) {
            console.error('Ошибка изменения количества', res)
            return
        }
        await useCartStore.getState().loadItems()
    }

    const cardHeader = (
        <div className="relative w-full h-full">
            <button
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setCurrent((p) => Math.max(0, p - 1))
                }}
                disabled={current === 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full z-10 md:hidden lg:block disabled:opacity-30"
            >
                <FiChevronLeft size={20} />
            </button>

            <button
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setCurrent((p) => Math.min(imagesSafe.length - 1, p + 1))
                }}
                disabled={current === imagesSafe.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full z-10 md:hidden lg:block disabled:opacity-30"
            >
                <FiChevronRight size={20} />
            </button>
            <Link href={`/catalog/product/${latin}`}>
                <div
                    ref={containerRef}
                    onMouseMove={handleMouseMove}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    className="relative rounded-tl-lg rounded-tr-lg overflow-hidden w-[200] aspect-2/3 touch-pan-y"
                >
                    {isLoading ? (
                        <Skeleton className="w-full h-full aspect-2/3 rounded-t-lg bg-gray-200 animate-pulse" />
                    ) : imagesSafe?.length > 0 ? (
                        <div className="w-full h-full touch-pan-y relative">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={
                                        imgError[current]
                                            ? `fallback-${current}`
                                            : current
                                    }
                                    initial={{ opacity: 0, x: 40 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -40 }}
                                    transition={{ duration: 0.3 }}
                                    className="absolute inset-0 w-full h-full"
                                >
                                    <img
                                        src={`${process.env.NEXT_PUBLIC_IMG_URL}/${imagesSafe[current]}`}
                                        alt={name}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        draggable={false}
                                        onError={() => handleImgError(current)}
                                    />
                                </motion.div>
                            </AnimatePresence>

                            {/* точки */}
                            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                                {imagesSafe.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrent(i)}
                                        className={classNames(
                                            'w-2.5 h-2.5 rounded-full transition-all',
                                            i === current
                                                ? 'bg-black'
                                                : 'bg-gray-300 hover:bg-gray-400',
                                        )}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        // ✅ ЗАГЛУШКА ТОЛЬКО ДЛЯ КАРТИНКИ
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                            <MdOutlineImageNotSupported className="w-14 h-14 text-gray-300" />
                        </div>
                    )}
                </div>
            </Link>
        </div>
    )

    const cardFooter = (
        <div className="flex flex-col items-center">
            <div className="flex flex-col gap-2 justify-end mr-4 w-full lg:flex-row mb-3 text-black">
                    <strong className='text-xl font-bold'> {price.toLocaleString('ru-RU')} ₽</strong>
            </div>
            {isError ? (
                <div className="flex border py-3 px-2 rounded-lg border-red-500 bg-red-50 items-center gap-2">
                    <span className="text-red-600 text-sm">
                        Ошибка при добавлении
                    </span>
                    <MdErrorOutline size={22} className="text-red-600" />
                </div>
            ) : cartItem ? (
                <div className="flex items-center justify-between w-full border border-gray-300 rounded-sm overflow-hidden">
                    <button
                        onClick={() =>
                            changeQty(cartItem.id, Math.max(1, quantity - 1))
                        }
                        className="  w-10 h-10 flex items-center justify-center  text-lg font-medium hover:bg-gray-100 transition"
                    >
                        <FaMinus />
                    </button>
                    <span className="min-w-[2rem] text-center py-[10px] px-[4px] text-lg text-gray-800">
                        <span>{quantity}</span>
                    </span>
                    <button
                        onClick={() => changeQty(cartItem.id, quantity + 1)}
                        className="w-10 h-10
                                                flex items-center justify-center text-lg font-medium hover:bg-gray-100 transition"
                    >
                        <FaPlus />
                    </button>
                </div>
            ) : (
                <Button
                    className="mr-2 w-full bg-black text-white hover:text-white"
                    shape="round"
                    onClick={(e) => handleAdd(variantId, 1, e)}
                >
                    В корзину
                </Button>
            )}
        </div>
    )

    return (
        <div className="w-full flex flex-col">
            <Card
                clickable
                className="min-h-[450px] hover:shadow-lg transition duration-150 ease-in-out dark:border dark:border-gray-600 dark:border-solid flex flex-col justify-between items-center h-full p-2 border rounded-xl"
                header={{
                    content: cardHeader,
                    bordered: false,
                }}
                footer={{
                    content: cardFooter,
                    bordered: false,
                }}
            >
                <Link href={`/catalog/product/${latin}`}>
                    <span className="text-xs">{vendorCode}</span>
                    <h2 className="font-bold my-3 text-xl leading-tight font-bold text-gray-900">
                        {name}
                    </h2>
                    <p className="line-clamp-3">{description}</p>
                </Link>
            </Card>
        </div>
    )
}
